import {
    Box,
    zIndex,
    color,
    radius,
    Flex,
    palette,
    Group,
    spacing,
} from '@landr/maestro';
import styled, { css } from 'styled-components';
import { Spacer } from '../constant';

export const PreviewOverlay = styled(Box)<{ isPreviewVisible: boolean }>`
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;

    display: none;
    opacity: 0;

    ${({ isPreviewVisible }) =>
        isPreviewVisible &&
        `
        display: block;
        z-index: 1;
    `};
`;

export const PreviewStyled = styled(Flex)<{
    isTopOffsetRequired: boolean;
    isPreviewVisible: boolean;
}>`
    display: none;
    opacity: 0;
    pointer-events: none;

    position: absolute;
    top: ${({ isTopOffsetRequired, theme }) =>
        isTopOffsetRequired
            ? theme.spacing('xxl') // to match the carousel tabs height
            : `-${theme.spacing('lg')}`};
    left: -${Spacer}px;
    right: -${Spacer}px;

    width: 100%;
    width: calc(100% + ${Spacer}px + ${Spacer}px);

    z-index: ${zIndex('subOverlay')};

    @keyframes fadeIn {
        from {
            opacity: 0;
        }

        to {
            opacity: 1;
        }
    }

    ${({ isPreviewVisible }) =>
        isPreviewVisible &&
        `
        display: block;
        animation-duration: 250ms;
        animation-name: fadeIn;
        opacity: 1;  
    `};
`;

export const PreviewTop = styled.div<{ artworkLeft: number }>`
    position: relative;
    display: inline-flex;
    /* @maestro-no-token */
    z-index: 2;
    ${({ artworkLeft }) => `left: ${artworkLeft}px;`};
    width: 0;
`;

export const PreviewTopContainer = styled.div<{
    backgroundColor: string;
}>`
    pointer-events: all;
    position: relative;
    display: inline-block;

    padding: 12px;
    padding-bottom: 0;

    border-top-left-radius: ${radius('base')};
    border-top-right-radius: ${radius('base')};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    background: linear-gradient(
            180deg,
            rgba(95, 99, 174, 0.48) 0%,
            rgba(95, 99, 174, 0.33) 100%
        ),
        ${color('neutral.800')};

    background: linear-gradient(
            180deg,
            ${({ backgroundColor }) => `${backgroundColor}`}7A 0%,
            ${({ backgroundColor }) => `${backgroundColor}`}54 100%
        ),
        ${color('neutral.800')};
`;

export const PreviewArtworkPlaceholder = styled.div<{ artworkWidth: number }>`
    position: relative;
    background-color: transparent;
    border-radius: ${radius('base')};
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24);

    ${({ artworkWidth }) => `width: calc( ${artworkWidth}px + ${Spacer}px );`};
    ${({ artworkWidth }) => `height: calc( ${artworkWidth}px + ${Spacer}px )`};
`;

export const PreviewBottom = styled.div<{
    backgroundColor: string;
}>`
    pointer-events: all;
    position: relative;
    z-index: 1;
    display: block;
    margin: 0;
    top: -12px;
    padding: ${spacing('xxl')};

    min-height: 336px;

    border-radius: ${radius('base')};

    background: linear-gradient(
            180deg,
            rgba(95, 99, 174, 0.33) 0%,
            rgba(95, 99, 174, 0) 100%
        ),
        ${color('neutral.800')};

    background: linear-gradient(
            180deg,
            ${({ backgroundColor }) => `${backgroundColor}`}54 0%,
            ${({ backgroundColor }) => `${backgroundColor}`}00 100%
        ),
        ${color('neutral.800')};
`;

export const StyledBackgroundImage = styled(Box)`
    overflow: hidden;
    width: 100%;
    height: 100%;
    position: absolute;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24);
    border-radius: ${radius('base')};
    opacity: 0.32;
    opacity: 1;
`;

export const TagsAndWaveform = styled(Group.Item)<{ isLoading?: boolean }>`
    overflow: hidden;
    flex-grow: 1;

    ${({ isLoading }) =>
        !isLoading &&
        `
        margin-bottom: -6px; /* needed to center align with other group.item */
    `};
`;

export const WaveformContainer = styled(Box)`
    width: 100%;
    height: 20px;
`;

export const FlexStretch = styled(Group)`
    width: 100%;
    max-width: 100%;
`;

export const PreviewSamplesRowItem = styled(Group.Item)<{
    isLoading?: boolean;
    isCurrentlyFocused?: boolean;
}>`
    padding: ${spacing('sm')} ${spacing('xs')};

    border: 1px solid transparent;
    border-bottom-color: ${palette('neutral.0', 0.12)};

    :last-of-type {
        border-bottom-color: transparent;
    }

    :focus-visible {
        outline: none;
    }

    ${({ isLoading }) =>
        !isLoading &&
        css`
            :hover {
                border: 1px solid transparent;
                border-radius: ${radius('base')};
                background-color: ${palette('neutral.0', 0.16)};
            }
        `};

    ${({ isCurrentlyFocused }) =>
        isCurrentlyFocused &&
        css`
            border: 1px solid transparent;
            border-radius: ${radius('base')};
            background-color: ${palette('neutral.0', 0.16)};
        `};
`;

export const PreviewSamplesRowGroup = styled(Group)`
    align-items: center;
    user-select: none;
`;

export const PreviewSamplesWrapper = styled(Group.Item)`
    width: calc(100% - 320px - ${spacing('xxl')});
`;

export const PreviewSamplesColumn = styled(Group.Item)`
    width: calc(50% - 8px);

    :only-child {
        width: 100%;
    }
`;
